import { z } from 'zod';
import { NotificationAudienceExpressionSchema } from './notification-audience.schema.js';
import { NotificationConditionSchema } from './notification-condition.schema.js';
import {
    NOTIFICATION_CUSTOMIZATION_LEVELS,
    NOTIFICATION_DELIVERY_CLASSES,
    NOTIFICATION_MANAGEMENT_AREAS,
    NOTIFICATION_MANAGEMENT_KINDS,
    NOTIFICATION_POLICY_CHANNELS,
    NOTIFICATION_POLICY_PRIORITIES,
    NOTIFICATION_ROUTING_MODES,
    NotificationLocalizedTextSchema,
} from './notification-event-definition.schema.js';

const idSchema = z.string().min(1).max(128);
const codeSchema = z.string().min(3).max(120).regex(/^[A-Z][A-Z0-9_]*$/);

export const NotificationPolicyContentSourceSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('eventDefault') }).strict(),
    z.object({ mode: z.literal('notificationTemplate'), templateId: idSchema }).strict(),
]);

export const NotificationPolicyDraftConfigSchema = z.object({
    eventName: z.string().min(3).max(160).regex(/^[a-z][A-Za-z0-9]*(?:\.[a-z][A-Za-z0-9]*)+$/),
    eventDefinitionVersion: z.number().int().positive(),
    condition: NotificationConditionSchema,
    audience: NotificationAudienceExpressionSchema,
    channels: z.array(z.enum(NOTIFICATION_POLICY_CHANNELS)).min(1).max(4)
        .refine((channels) => new Set(channels).size === channels.length, 'notificationPolicy.duplicateChannel'),
    deliveryClass: z.enum(NOTIFICATION_DELIVERY_CLASSES),
    priority: z.enum(NOTIFICATION_POLICY_PRIORITIES),
    recipientCap: z.number().int().positive().max(1_000),
    contentSource: NotificationPolicyContentSourceSchema,
}).strict();

export const CreateNotificationPolicySchema = z.object({
    code: codeSchema,
    name: NotificationLocalizedTextSchema,
    description: NotificationLocalizedTextSchema,
    baseSystemPolicyCode: codeSchema.nullable().optional(),
    customization: z.enum(NOTIFICATION_CUSTOMIZATION_LEVELS).default('full'),
    customFields: z.record(z.unknown()).default({}),
    draft: NotificationPolicyDraftConfigSchema,
}).strict();

export const UpdateNotificationPolicyDraftSchema = z.object({
    expectedRevisionNumber: z.number().int().positive(),
    expectedUpdatedAt: z.string().datetime().optional(),
    name: NotificationLocalizedTextSchema.optional(),
    description: NotificationLocalizedTextSchema.optional(),
    customFields: z.record(z.unknown()).optional(),
    draft: NotificationPolicyDraftConfigSchema,
}).strict();

export const NOTIFICATION_POLICY_WARNING_ACKNOWLEDGMENTS = Object.freeze(['NO_MATCH', 'ZERO_RECIPIENTS', 'REQUIRED_DELIVERY']);

export const PublishNotificationPolicySchema = z.object({
    draftRevisionId: idSchema,
    expectedActiveRevisionId: idSchema.nullable(),
    comment: z.string().trim().min(1).max(1_000),
    verificationId: idSchema.optional(),
    warningAcknowledgments: z.array(z.enum(NOTIFICATION_POLICY_WARNING_ACKNOWLEDGMENTS)).max(3)
        .refine((values) => new Set(values).size === values.length, 'notificationPolicy.duplicateAcknowledgment')
        .default([]),
    useAfterPublish: z.literal(true).default(true),
}).strict();

export const RollbackNotificationPolicySchema = z.object({
    targetRevisionId: idSchema,
    expectedActiveRevisionId: idSchema.nullable(),
    comment: z.string().trim().min(1).max(1_000),
    warningAcknowledgments: z.array(z.literal('REQUIRED_DELIVERY')).max(1).default([]),
}).strict();

export const ValidateNotificationPolicySchema = z.object({
    draftRevisionId: idSchema,
}).strict();

export const SimulateNotificationPolicySchema = z.object({
    draftRevisionId: idSchema,
    source: z.discriminatedUnion('type', [
        z.object({ type: z.literal('synthetic') }).strict(),
        z.object({ type: z.literal('record'), recordId: idSchema }).strict(),
        z.object({ type: z.literal('dispatch'), dispatchId: idSchema }).strict(),
    ]),
}).strict();

export const InspectNotificationPolicyAudienceSchema = SimulateNotificationPolicySchema.extend({
    expectedRevisionChecksum: z.string().regex(/^[a-f0-9]{64}$/),
}).strict();

export const NotificationPolicyVerificationParamsSchema = z.object({
    id: idSchema,
    verificationId: idSchema,
}).strict();

export const InspectNotificationPolicyVerificationRecipientsSchema = z.object({}).strict();

const checksumSchema = z.string().regex(/^[a-f0-9]{64}$/);
const recordSourceSchema = z.object({ type: z.literal('record'), recordId: idSchema }).strict();
const explicitChannelsSchema = z.array(z.enum(NOTIFICATION_POLICY_CHANNELS)).min(1).max(4)
    .refine((channels) => new Set(channels).size === channels.length, 'notificationPolicy.duplicateChannel');

export const PreviewDeliverNotificationPolicySchema = z.object({
    draftRevisionId: idSchema,
    source: recordSourceSchema,
    idempotencyKey: z.string().uuid(),
    expectedRevisionChecksum: checksumSchema,
    testerIds: z.array(idSchema).max(5)
        .refine((values) => new Set(values).size === values.length, 'notificationPolicy.duplicateTester')
        .optional(),
    channels: explicitChannelsSchema,
}).strict();

export const LIVE_PILOT_CONFIRMATION = 'LIVE PILOT';
export const LivePilotNotificationPolicySchema = z.object({
    draftRevisionId: idSchema,
    verificationId: idSchema,
    idempotencyKey: z.string().uuid(),
    expectedDraftRevisionChecksum: checksumSchema,
    expectedCandidateChecksum: checksumSchema,
    expectedContentChecksum: checksumSchema,
    expectedContextChecksum: checksumSchema,
    expectedPreviewChecksum: checksumSchema,
    expectedRecipientCount: z.number().int().positive().max(100),
    expectedChannels: explicitChannelsSchema,
    confirmation: z.literal(LIVE_PILOT_CONFIRMATION),
}).strict();

// Temporary compatibility input. The service adapts this to a current-admin-only preview.
export const TestDeliverNotificationPolicySchema = z.object({
    draftRevisionId: idSchema,
    source: recordSourceSchema,
    idempotencyKey: z.string().uuid(),
    expectedRecipientCount: z.number().int().positive().max(1_000),
    expectedRevisionChecksum: checksumSchema,
    expectedPreviewChecksum: checksumSchema,
}).strict();

export const NotificationDispatchResultSchema = z.object({
    dispatchId: idSchema,
    kind: z.enum(['test', 'preview', 'live-pilot']),
    status: z.enum(['queued', 'processing', 'partial', 'delivered', 'failed']),
    errorCode: z.string().max(120).nullable(),
    recipientCount: z.number().int().nonnegative().max(1_000),
    recipients: z.array(z.object({
        recipientRef: z.string().min(1).max(64),
        channels: z.array(z.object({
            channel: z.enum(NOTIFICATION_POLICY_CHANNELS),
            state: z.string().min(1).max(80),
            reason: z.string().max(120).nullable(),
        }).strict()).max(4),
    }).strict()).max(1_000),
}).strict();

export const NOTIFICATION_TENANT_POLICY_MODES = Object.freeze(['inherit', 'custom', 'off']);

export const ChangeNotificationPolicyModeSchema = z.object({
    mode: z.enum(NOTIFICATION_TENANT_POLICY_MODES),
    expectedUpdatedAt: z.string().datetime().optional(),
    comment: z.string().trim().min(1).max(1_000),
}).strict();

export const ToggleNotificationPolicySchema = ChangeNotificationPolicyModeSchema.omit({ mode: true }).strict();

export const DeleteNotificationPolicyDraftSchema = z.object({
    draftRevisionId: idSchema,
    expectedRevisionNumber: z.number().int().positive(),
    expectedUpdatedAt: z.string().datetime().optional(),
}).strict();

export const NotificationPolicyListQuerySchema = z.object({
    eventName: z.string().min(3).max(160).optional(),
    origin: z.enum(['system', 'tenant']).optional(),
    enabled: z.enum(['true', 'false']).optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().positive().max(100).default(25),
}).strict();

const exactNotificationEventNameSchema = z.string().min(3).max(160)
    .regex(/^[a-z][A-Za-z0-9]*(?:\.[a-z][A-Za-z0-9]*)+$/);

export const NOTIFICATION_MANAGEMENT_READINESS_FILTERS = Object.freeze([
    'none', 'any', 'validation', 'stale', 'dependency',
]);

export const NotificationCatalogQuerySchema = z.object({
    search: z.string().trim().min(1).max(120).optional(),
    module: z.string().trim().min(1).max(80).optional(),
    managementArea: z.enum(NOTIFICATION_MANAGEMENT_AREAS).optional(),
    managementKind: z.enum(NOTIFICATION_MANAGEMENT_KINDS).optional(),
    rolloutMode: z.enum(NOTIFICATION_ROUTING_MODES).optional(),
    page: z.coerce.number().int().positive().max(10_000).default(1),
    limit: z.coerce.number().int().positive().max(100).default(25),
}).strict();

export const NotificationManagementEventListQuerySchema = z.object({
    search: z.string().trim().min(1).max(120).optional(),
    pageKeys: z.string().trim().min(1).max(4_000).regex(/^[^,\s]+(?:,[^,\s]+)*$/).optional(),
    eventAction: z.string().trim().min(1).max(80).regex(/^[a-z][A-Za-z0-9]*$/).optional(),
    module: z.string().trim().min(1).max(80).optional(),
    managementArea: z.enum(NOTIFICATION_MANAGEMENT_AREAS).optional(),
    managementKind: z.enum(NOTIFICATION_MANAGEMENT_KINDS).optional(),
    rolloutMode: z.enum(NOTIFICATION_ROUTING_MODES).optional(),
    effectiveBehavior: z.enum(['system', 'tenant', 'suppressed']).optional(),
    draft: z.enum(['present', 'absent']).optional(),
    stale: z.enum(['none', 'any', 'base', 'template']).optional(),
    customization: z.enum(NOTIFICATION_CUSTOMIZATION_LEVELS).optional(),
    readinessIssue: z.enum(NOTIFICATION_MANAGEMENT_READINESS_FILTERS).optional(),
    visibility: z.enum(['essential']).optional(),
    page: z.coerce.number().int().positive().max(10_000).default(1),
    limit: z.coerce.number().int().positive().max(100).default(25),
}).strict();

export const NotificationManagementEventWorkspaceParamsSchema = z.object({
    eventName: exactNotificationEventNameSchema,
}).strict();

export const NotificationRecordSourceParamsSchema = z.object({
    eventName: exactNotificationEventNameSchema,
}).strict();

export const NotificationRecordSourceQuerySchema = z.object({
    search: z.string().trim().min(1).max(120).optional(),
    page: z.coerce.number().int().positive().max(10_000).default(1),
    limit: z.coerce.number().int().positive().max(25).default(10),
}).strict();

export const NotificationRecordSourceItemSchema = z.object({
    id: z.string().min(1).max(160),
    label: z.union([
        z.string().trim().min(1).max(240),
        NotificationLocalizedTextSchema,
    ]),
    updatedAt: z.string().datetime().nullable(),
}).strict();

export const NotificationRecordSourceResultSchema = z.object({
    available: z.boolean(),
    reasonCode: z.enum([
        'RECORD_SOURCE_UNAVAILABLE',
        'RECORD_MODEL_UNAVAILABLE',
        'DETAIL_PREVIEW_UNSUPPORTED',
        'SPECIALTY_PREVIEW_UNSUPPORTED',
    ]).nullable(),
    data: z.array(NotificationRecordSourceItemSchema).max(25),
    page: z.number().int().positive().max(10_000),
    limit: z.number().int().positive().max(25),
    total: z.number().int().nonnegative().max(1_000_000_000),
    hasMore: z.boolean(),
    truncated: z.boolean(),
    scanned: z.number().int().nonnegative().max(1_000_000_000),
}).strict();

export const NotificationPolicyEffectiveQuerySchema = z.object({
    eventName: z.string().min(3).max(160).optional(),
}).strict();

export const NotificationRecoveryParamsSchema = z.object({
    dispatchId: idSchema,
    intentId: idSchema,
}).strict();

const recoveryExpectedAttemptNumberSchema = z.number().int().nonnegative().max(1_000_000);
const recoveryNoteSchema = z.string().trim().min(1).max(1_000);

export const RecoverNotificationDeliverySchema = z.discriminatedUnion('action', [
    z.object({
        action: z.literal('retry'),
        expectedState: z.enum(['scheduled', 'failed']),
        expectedAttemptNumber: recoveryExpectedAttemptNumberSchema,
    }).strict(),
    z.object({
        action: z.literal('reconcile'),
        expectedState: z.literal('unknown'),
        expectedAttemptNumber: recoveryExpectedAttemptNumberSchema,
    }).strict(),
    z.object({
        action: z.literal('resolve'),
        expectedState: z.literal('unknown'),
        expectedAttemptNumber: recoveryExpectedAttemptNumberSchema,
        state: z.enum(['sent', 'failed', 'cancelled']),
        comment: recoveryNoteSchema,
        evidence: recoveryNoteSchema,
    }).strict(),
]);

export const NotificationDispatchListQuerySchema = z.object({
    eventName: z.string().min(3).max(160).optional(),
    kind: z.enum(['event', 'test', 'preview', 'live-pilot']).optional(),
    status: z.enum(['no_match', 'blocked', 'suppressed', 'queued', 'processing', 'partial', 'delivered', 'failed']).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().positive().max(100).default(25),
}).strict().superRefine((value, ctx) => {
    if (value.from && value.to && new Date(value.from) > new Date(value.to)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'notificationRouting.invalidRange' });
    }
});
